import { useState } from "react";
import { Loader2, Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ANY = "Any upcoming game";

export const NotifyForm = ({
  open,
  onOpenChange,
  initialAppInterest = "",
  appOptions = [],
}) => {
  const [data, setData] = useState({
    name: "",
    email: "",
    app_interest: initialAppInterest || ANY,
    message: "",
    company: "",
  });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) =>
    setData((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = data.name.trim();
    const email = data.email.trim();
    if (!name || !email) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/submissions", {
        type: "notify",
        name,
        email,
        app_interest: data.app_interest || ANY,
        message: data.message.trim() || null,
        company: data.company || "",
      });
      toast.success("You’re on the list — we’ll ping you on launch day.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to sign up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const options = Array.from(new Set([ANY, ...appOptions.filter(Boolean)]));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (busy) return; onOpenChange(v); }}>
      <DialogContent data-testid="notify-form-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="pf-display">Get release pings</DialogTitle>
          <DialogDescription>
            Drop your email and we’ll let you know the moment it launches — no spam, ever.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="notify-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nf-name">Name</Label>
              <Input id="nf-name" data-testid="notify-form-name" value={data.name} onChange={onChange("name")} autoComplete="name" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nf-email">Email</Label>
              <Input id="nf-email" type="email" data-testid="notify-form-email" value={data.email} onChange={onChange("email")} autoComplete="email" required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nf-app">Interested in</Label>
            <Select
              value={data.app_interest}
              onValueChange={(v) => setData({ ...data, app_interest: v })}
            >
              <SelectTrigger id="nf-app" data-testid="notify-form-app-interest">
                <SelectValue placeholder="Choose an app" />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nf-msg">Anything you’d like us to know? (optional)</Label>
            <Textarea id="nf-msg" data-testid="notify-form-message" rows={3} value={data.message} onChange={onChange("message")} placeholder="e.g. iOS / Android, feature requests, fav genres" />
          </div>

          {/* Honeypot */}
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", top: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          >
            <Label htmlFor="nf-company">Company</Label>
            <Input id="nf-company" tabIndex={-1} autoComplete="off" value={data.company} onChange={onChange("company")} />
          </div>

          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="notify-form-submit" className="rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
              Notify me
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyForm;
